/**
 * Per-message receipt aggregation backed by IndexedDB.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/ReceiptStore.swift`.
 *
 * Mirrors iOS semantics exactly:
 *   - read is a strict upgrade over delivered; a stale delivered arriving
 *     after a read is discarded so the UI cannot regress.
 *   - observedAt is the local clock; receiptTs is the peer-claimed time.
 *     UI should prefer observedAt for ordering.
 *   - sendReadReceiptsEnabled defaults to true; persisted in localStorage
 *     (replaces iOS UserDefaults) under "fr.kaether.receipts.sendReadReceipts".
 *
 * Privacy:
 *   - Delivered receipts are always emitted (they leak nothing the sender
 *     doesn't already know about transport-level delivery).
 *   - Read receipts are gated on sendReadReceiptsEnabled.
 *
 * Storage:
 *   - IndexedDB via idb-keyval, one entry per messageId.
 *   - Key = "rcpt:<sha256hex(messageId)>" — mirrors iOS's SHA256-named files.
 *   - iOS uses file-per-message + SHA256 file name; we mirror the SHA256
 *     keying so both platforms use the same stable identifier derivation.
 *     PROPOSAL FOR IOS: consider adding a small in-memory LRU cache here
 *     since IDB reads are async; iOS already benefits from OS page-cache.
 */

import { get, set, del, createStore } from "idb-keyval";
import type { Receipt } from "./Receipt";

// ---------------------------------------------------------------------------
// Value types (mirrors iOS ReceiptHighestState, ReceiptRecord, MessageReceipts)
// ---------------------------------------------------------------------------

export type ReceiptHighestState = "delivered" | "read";

const stateRank: Record<ReceiptHighestState, number> = {
  delivered: 0,
  read: 1,
};

function isUpgrade(
  incoming: ReceiptHighestState,
  existing: ReceiptHighestState,
): boolean {
  return stateRank[incoming] > stateRank[existing];
}

export interface ReceiptRecord {
  readonly userId: string;
  readonly highest: ReceiptHighestState;
  /** Peer-claimed Unix epoch seconds. */
  readonly receiptTs: number;
  /** Local ISO-8601 timestamp when we recorded this receipt. */
  readonly observedAt: string;
}

export interface MessageReceipts {
  /** userId → highest state seen for that user on this message. */
  readonly perUser: Readonly<Record<string, ReceiptRecord>>;
}

export const EMPTY_MESSAGE_RECEIPTS: MessageReceipts = { perUser: {} };

export function deliveredBy(r: MessageReceipts): Set<string> {
  return new Set(
    Object.values(r.perUser)
      .filter((rec) => stateRank[rec.highest] >= stateRank["delivered"])
      .map((rec) => rec.userId),
  );
}

export function readBy(r: MessageReceipts): Set<string> {
  return new Set(
    Object.values(r.perUser)
      .filter((rec) => stateRank[rec.highest] >= stateRank["read"])
      .map((rec) => rec.userId),
  );
}

export function isEmptyMessageReceipts(r: MessageReceipts): boolean {
  return Object.keys(r.perUser).length === 0;
}

// ---------------------------------------------------------------------------
// Toggle key — mirrors iOS UserDefaults key
// ---------------------------------------------------------------------------

const READ_RECEIPTS_TOGGLE_KEY =
  "fr.kaether.receipts.sendReadReceipts" as const;

// ---------------------------------------------------------------------------
// IDB store
// ---------------------------------------------------------------------------

/**
 * Named IDB store so receipt data is isolated from other e2ee IDB usage.
 * Mirrors iOS's dedicated "receipts/" directory.
 */
function makeIdbStore(dbName: string) {
  return createStore(dbName, "receipts");
}

// ---------------------------------------------------------------------------
// SHA-256 key derivation (mirrors iOS SHA256-named files)
// ---------------------------------------------------------------------------

async function sha256Hex(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function idbKey(messageId: string): Promise<string> {
  return `rcpt:${await sha256Hex(messageId)}`;
}

// ---------------------------------------------------------------------------
// ReceiptStore
// ---------------------------------------------------------------------------

/**
 * Options for constructing a ReceiptStore. Primarily used by tests to inject
 * isolated IDB store instances and storage backends.
 */
export interface ReceiptStoreOptions {
  /**
   * idb-keyval-compatible store returned by createStore(). Defaults to a
   * shared named store `"fr.kaether.receipts"`.
   */
  idbStore?: ReturnType<typeof createStore>;
  /**
   * A key-value backend for the read-receipts toggle (default: localStorage).
   * Must expose `getItem`, `setItem`, and `removeItem`.
   */
  toggleStorage?: Pick<Storage, "getItem" | "setItem" | "removeItem">;
}

export class ReceiptStore {
  private readonly idb: ReturnType<typeof createStore>;
  private readonly toggleStorage: Pick<
    Storage,
    "getItem" | "setItem" | "removeItem"
  >;

  static readonly shared: ReceiptStore = new ReceiptStore();

  constructor(options: ReceiptStoreOptions = {}) {
    this.idb =
      options.idbStore ?? makeIdbStore("fr.kaether.receipts");
    this.toggleStorage = options.toggleStorage ?? localStorage;
    // First-run default: read receipts on. Only set if absent, so the user's
    // choice persists across sessions. Mirrors iOS UserDefaults first-run
    // default in ReceiptStore.init.
    if (this.toggleStorage.getItem(READ_RECEIPTS_TOGGLE_KEY) === null) {
      this.toggleStorage.setItem(READ_RECEIPTS_TOGGLE_KEY, "true");
    }
  }

  // -------------------------------------------------------------------------
  // Privacy toggle
  // -------------------------------------------------------------------------

  get sendReadReceiptsEnabled(): boolean {
    return this.toggleStorage.getItem(READ_RECEIPTS_TOGGLE_KEY) !== "false";
  }

  set sendReadReceiptsEnabled(value: boolean) {
    this.toggleStorage.setItem(READ_RECEIPTS_TOGGLE_KEY, value ? "true" : "false");
  }

  // -------------------------------------------------------------------------
  // Recording
  // -------------------------------------------------------------------------

  /**
   * Record a receipt observation. Returns the resulting MessageReceipts, or
   * null if the incoming state was a no-op downgrade and was discarded.
   *
   * Mirrors iOS `ReceiptStore.record(receipt:from:at:)`.
   */
  async record(
    receipt: Receipt,
    fromUserId: string,
    observedAt: Date = new Date(),
  ): Promise<MessageReceipts | null> {
    const key = await idbKey(receipt.messageId);
    const incoming: ReceiptHighestState =
      receipt.type === "read" ? "read" : "delivered";

    const current: MessageReceipts =
      (await get<MessageReceipts>(key, this.idb)) ?? EMPTY_MESSAGE_RECEIPTS;
    const existing = current.perUser[fromUserId];

    if (existing !== undefined && !isUpgrade(incoming, existing.highest)) {
      // Equal-or-lower state — no monotonic change. Discard so stale
      // duplicates don't churn observedAt. Mirrors iOS behaviour exactly.
      return null;
    }

    const updated: MessageReceipts = {
      perUser: {
        ...current.perUser,
        [fromUserId]: {
          userId: fromUserId,
          highest: incoming,
          receiptTs: receipt.ts,
          observedAt: observedAt.toISOString(),
        },
      },
    };
    await set(key, updated, this.idb);
    return updated;
  }

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  async receipts(messageId: string): Promise<MessageReceipts> {
    const key = await idbKey(messageId);
    return (await get<MessageReceipts>(key, this.idb)) ?? EMPTY_MESSAGE_RECEIPTS;
  }

  async deliveredBy(messageId: string): Promise<Set<string>> {
    return deliveredBy(await this.receipts(messageId));
  }

  async readBy(messageId: string): Promise<Set<string>> {
    return readBy(await this.receipts(messageId));
  }

  // -------------------------------------------------------------------------
  // Reset
  // -------------------------------------------------------------------------

  async clear(messageId: string): Promise<void> {
    const key = await idbKey(messageId);
    await del(key, this.idb);
  }

  /**
   * Wipe all receipt data from the IDB store. Does not touch the toggle.
   * Mirrors iOS `ReceiptStore.resetAll()`.
   */
  async resetAll(): Promise<void> {
    // idb-keyval does not expose a clearStore(), so we open the raw IDB and
    // call clear() on the object store.
    await new Promise<void>((resolve, reject) => {
      // Access the underlying IDBDatabase via a no-op transaction to get a
      // reference; then open a readwrite transaction on the known store name.
      const req = indexedDB.open("fr.kaether.receipts");
      req.onsuccess = () => {
        const db = req.result;
        const storeNames = Array.from(db.objectStoreNames);
        if (storeNames.length === 0) {
          db.close();
          resolve();
          return;
        }
        const tx = db.transaction(storeNames[0], "readwrite");
        tx.objectStore(storeNames[0]).clear();
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      };
      req.onerror = () => reject(req.error);
    });
  }
}
